import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Check, X, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

/**
 * Inline note editor attached to a booking.
 * Uses PUT /admin/bookings/{id}/edit or /it-admin/bookings/{id}/edit
 * to persist the `notes` field.
 */
export default function BookingNoteEditor({ booking, isItAdmin = false, compact = false, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(booking.notes || booking.admin_notes || "");

  const getAuthHeaders = () => {
    const token = localStorage.getItem(isItAdmin ? "it_admin_token" : "adminToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const save = async () => {
    setSaving(true);
    try {
      const endpoint = isItAdmin
        ? `${API}/it-admin/bookings/${booking.id}/edit`
        : `${API}/admin/bookings/${booking.id}/edit`;
      await axios.put(endpoint, { notes: value }, getAuthHeaders());
      toast.success("Note saved");
      setEditing(false);
      if (onUpdated) onUpdated(value);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save note");
    }
    setSaving(false);
  };

  const currentNote = booking.notes || booking.admin_notes || "";

  if (!editing) {
    return (
      <div className={`flex items-start gap-2 ${compact ? "text-sm" : "text-base"}`} data-testid={`note-view-${booking.id}`}>
        <StickyNote className={`${compact ? "w-4 h-4" : "w-5 h-5"} text-amber-500 flex-shrink-0 mt-0.5 drop-shadow-sm`} />
        {currentNote ? (
          <div className="flex-1 min-w-0">
            <p
              className={`whitespace-pre-wrap break-words rounded-lg px-3 py-2 font-bold leading-snug text-amber-900 bg-gradient-to-r from-yellow-200 to-amber-200 border-2 border-amber-400 shadow-sm ${compact ? "text-sm" : "text-base"}`}
              data-testid={`note-text-${booking.id}`}
            >
              {currentNote}
            </p>
            <button
              onClick={() => { setValue(currentNote); setEditing(true); }}
              className="text-xs text-amber-700 hover:text-amber-900 mt-1 flex items-center gap-1 font-semibold"
              data-testid={`note-edit-${booking.id}`}
            >
              <Pencil className="w-3 h-3" /> Edit note
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="text-sm text-muted-foreground hover:text-amber-700 italic underline decoration-dotted font-medium"
            data-testid={`note-add-${booking.id}`}
          >
            + Add note
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid={`note-editor-${booking.id}`}>
      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Private note visible only to admins..."
        className="min-h-[72px] text-sm font-semibold text-amber-900 bg-amber-50 border-2 border-amber-400 rounded-lg focus-visible:ring-amber-500"
        maxLength={500}
        autoFocus
        onClick={(e) => e.stopPropagation()}
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={(e) => { e.stopPropagation(); save(); }} disabled={saving} className="h-7 px-3 bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid={`note-save-${booking.id}`}>
          <Check className="w-3 h-3 mr-1" /> {saving ? "Saving..." : "Save"}
        </Button>
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setEditing(false); }} className="h-7 px-3 text-xs">
          <X className="w-3 h-3 mr-1" /> Cancel
        </Button>
        <span className="text-[10px] text-muted-foreground ml-auto">{value.length}/500</span>
      </div>
    </div>
  );
}
